import { IEnhancedNetworkRequest, IEnhancedNetworkResponse, INetworkTiming } from './types';
import { generateUniqueId } from '../utils';

/**
 * Axios 인터셉터 - axios 라이브러리와의 통합
 */
export class AxiosInterceptor {
  private _isIntercepting: boolean = false;
  private _requestInterceptorId: number | null = null;
  private _responseInterceptorId: number | null = null;
  private _onRequest?: (request: IEnhancedNetworkRequest) => void;
  private _onResponse?: (response: IEnhancedNetworkResponse) => void;
  private _onError?: (error: Error, request: IEnhancedNetworkRequest) => void;

  constructor(callbacks?: {
    onRequest?: (request: IEnhancedNetworkRequest) => void;
    onResponse?: (response: IEnhancedNetworkResponse) => void;
    onError?: (error: Error, request: IEnhancedNetworkRequest) => void;
  }) {
    this._onRequest = callbacks?.onRequest;
    this._onResponse = callbacks?.onResponse;
    this._onError = callbacks?.onError;
  }

  /**
   * Axios 인터셉션 시작
   */
  public startIntercepting(): void {
    if (this._isIntercepting) {
      console.warn('[AxiosInterceptor] Already intercepting');
      return;
    }

    // axios가 전역에 있는지 확인
    const axios = this._getAxiosInstance();
    if (!axios) {
      console.warn('[AxiosInterceptor] Axios not found in global scope');
      return;
    }

    console.log('[AxiosInterceptor] Starting axios interception...');

    try {
      // 요청 인터셉터 등록
      this._requestInterceptorId = axios.interceptors.request.use(
        (config: any) => this._handleAxiosRequest(config),
        (error: any) => this._handleAxiosRequestError(error)
      );

      // 응답 인터셉터 등록
      this._responseInterceptorId = axios.interceptors.response.use(
        (response: any) => this._handleAxiosResponse(response),
        (error: any) => this._handleAxiosResponseError(error)
      );

      this._isIntercepting = true;
      console.log('[AxiosInterceptor] Axios interception started');
    } catch (error) {
      console.error('[AxiosInterceptor] Failed to start interception:', error);
    }
  }

  /**
   * Axios 인터셉션 중지
   */
  public stopIntercepting(): void {
    if (!this._isIntercepting) {
      return;
    }

    const axios = this._getAxiosInstance();
    if (!axios) {
      return;
    }

    console.log('[AxiosInterceptor] Stopping axios interception...');

    try {
      // 인터셉터 제거
      if (this._requestInterceptorId !== null) {
        axios.interceptors.request.eject(this._requestInterceptorId);
        this._requestInterceptorId = null;
      }

      if (this._responseInterceptorId !== null) {
        axios.interceptors.response.eject(this._responseInterceptorId);
        this._responseInterceptorId = null;
      }

      this._isIntercepting = false;
      console.log('[AxiosInterceptor] Axios interception stopped');
    } catch (error) {
      console.error('[AxiosInterceptor] Failed to stop interception:', error);
    }
  }

  /**
   * 전역 axios 인스턴스 탐지
   */
  private _getAxiosInstance(): any {
    // 1. 브라우저 환경에서 window.axios 확인
    if (typeof window !== 'undefined' && (window as any).axios) {
      return (window as any).axios;
    }

    // 2. Node.js 환경에서 global.axios 확인
    if (typeof global !== 'undefined' && (global as any).axios) {
      return (global as any).axios;
    }

    // 3. CommonJS require 시도 (동적으로, 하지만 안전하게)
    try {
      const moduleRequire = typeof require !== 'undefined' ? require : null;
      if (moduleRequire) {
        return moduleRequire('axios');
      }
    } catch (error) {
      // axios가 설치되지 않음 또는 require 실패
    }

    // 4. ES Module이 이미 로드되어 있는지 확인
    if (typeof window !== 'undefined' && (window as any).__AXIOS_INSTANCE__) {
      return (window as any).__AXIOS_INSTANCE__;
    }

    return null;
  }

  /**
   * Axios 요청 인터셉터
   */
  private _handleAxiosRequest(config: any): any {
    try {
      const requestId = generateUniqueId();
      const startTime = performance.now();

      // Axios config에서 요청 정보 추출
      const url = this._buildFullUrl(config);
      const method = (config.method || 'GET').toUpperCase();
      const headers = config.headers || {};

      // 요청 바디 처리
      let requestBody: string | undefined;
      let requestBodySize = 0;

      if (config.data) {
        if (typeof config.data === 'string') {
          requestBody = config.data;
          requestBodySize = new TextEncoder().encode(config.data).length;
        } else if (config.data instanceof FormData) {
          requestBody = '[FormData]';
          // FormData 크기는 정확히 측정하기 어려움
          requestBodySize = 0;
        } else {
          try {
            requestBody = JSON.stringify(config.data);
            requestBodySize = new TextEncoder().encode(requestBody).length;
          } catch (error) {
            requestBody = '[Unserializable Data]';
            requestBodySize = 0;
          }
        }
      }

      // 타이밍 정보
      const timing: INetworkTiming = {
        startTime,
        requestStart: startTime,
        responseStart: 0,
        responseEnd: 0,
        duration: 0,
      };

      // 강화된 네트워크 요청 객체 생성
      const enhancedRequest: IEnhancedNetworkRequest = {
        id: requestId,
        url,
        method,
        headers: this._normalizeHeaders(headers),
        body: requestBody,
        timestamp: Date.now(),
        timing,
        size: {
          requestHeaders: this._calculateHeadersSize(headers),
          requestBody: requestBodySize,
          total: this._calculateHeadersSize(headers) + requestBodySize,
        },
        graphql: this._detectGraphQL(headers, requestBody),
        priority: this._calculatePriority(url, method),
      };

      // config에 메타데이터 저장 (응답에서 사용하기 위해)
      config._debugTimeMachine = {
        requestId,
        startTime,
        enhancedRequest,
      };

      // 콜백 호출
      if (this._onRequest) {
        try {
          this._onRequest(enhancedRequest);
        } catch (error) {
          console.warn('[AxiosInterceptor] Error in onRequest callback:', error);
        }
      }

      return config;
    } catch (error) {
      console.error('[AxiosInterceptor] Error in request interceptor:', error);
      return config;
    }
  }

  /**
   * Axios 요청 에러 인터셉터
   */
  private _handleAxiosRequestError(error: any): Promise<any> {
    console.error('[AxiosInterceptor] Request error:', error);
    return Promise.reject(error);
  }

  /**
   * Axios 응답 인터셉터
   */
  private _handleAxiosResponse(response: any): any {
    try {
      const config = response.config;
      const metadata = config._debugTimeMachine;

      if (!metadata) {
        // 메타데이터가 없으면 추적 불가
        return response;
      }

      const responseEndTime = performance.now();
      
      // 타이밍 업데이트
      metadata.enhancedRequest.timing.responseStart = responseEndTime - 10; // 근사치
      metadata.enhancedRequest.timing.responseEnd = responseEndTime;
      metadata.enhancedRequest.timing.duration = responseEndTime - metadata.startTime;

      // 응답 바디 처리
      let responseBody: string | undefined;
      let responseBodySize = 0;

      if (response.data) {
        if (typeof response.data === 'string') {
          responseBody = response.data;
          responseBodySize = new TextEncoder().encode(response.data).length;
        } else {
          try {
            responseBody = JSON.stringify(response.data);
            responseBodySize = new TextEncoder().encode(responseBody).length;
          } catch (error) {
            responseBody = '[Unserializable Data]';
            responseBodySize = 0;
          }
        }
      }

      // 응답 헤더 처리
      const responseHeaders = this._normalizeHeaders(response.headers || {});

      // 강화된 네트워크 응답 객체 생성
      const enhancedResponse: IEnhancedNetworkResponse = {
        id: generateUniqueId(),
        requestId: metadata.requestId,
        status: response.status,
        statusText: response.statusText || '',
        headers: responseHeaders,
        body: responseBody,
        timestamp: Date.now(),
        timing: metadata.enhancedRequest.timing,
        size: {
          responseHeaders: this._calculateHeadersSize(responseHeaders),
          responseBody: responseBodySize,
          total: this._calculateHeadersSize(responseHeaders) + responseBodySize,
        },
        graphql: metadata.enhancedRequest.graphql ? 
          this._parseGraphQLResponse(responseBody) : undefined,
      };

      // 콜백 호출
      if (this._onResponse) {
        try {
          this._onResponse(enhancedResponse);
        } catch (error) {
          console.warn('[AxiosInterceptor] Error in onResponse callback:', error);
        }
      }

      return response;
    } catch (error) {
      console.error('[AxiosInterceptor] Error in response interceptor:', error);
      return response;
    }
  }

  /**
   * Axios 응답 에러 인터셉터
   */
  private _handleAxiosResponseError(error: any): Promise<any> {
    try {
      const config = error.config;
      const metadata = config?._debugTimeMachine;

      if (metadata) {
        const responseEndTime = performance.now();
        
        // 타이밍 업데이트
        metadata.enhancedRequest.timing.responseEnd = responseEndTime;
        metadata.enhancedRequest.timing.duration = responseEndTime - metadata.startTime;

        // 에러 응답 생성
        const enhancedResponse: IEnhancedNetworkResponse = {
          id: generateUniqueId(),
          requestId: metadata.requestId,
          status: error.response?.status || 0,
          statusText: error.response?.statusText || error.message,
          headers: this._normalizeHeaders(error.response?.headers || {}),
          body: error.message,
          timestamp: Date.now(),
          timing: metadata.enhancedRequest.timing,
          size: {
            responseHeaders: this._calculateHeadersSize(error.response?.headers || {}),
            responseBody: error.message.length,
            total: this._calculateHeadersSize(error.response?.headers || {}) + error.message.length,
          },
        };

        // 에러 콜백 호출
        if (this._onError) {
          try {
            this._onError(error, metadata.enhancedRequest);
          } catch (callbackError) {
            console.warn('[AxiosInterceptor] Error in onError callback:', callbackError);
          }
        }

        // 응답 콜백도 호출 (에러 응답도 응답이므로)
        if (this._onResponse) {
          try {
            this._onResponse(enhancedResponse);
          } catch (callbackError) {
            console.warn('[AxiosInterceptor] Error in onResponse callback:', callbackError);
          }
        }
      }
    } catch (interceptorError) {
      console.error('[AxiosInterceptor] Error in response error interceptor:', interceptorError);
    }

    return Promise.reject(error);
  }

  /**
   * 전체 URL 구성
   */
  private _buildFullUrl(config: any): string {
    const baseURL = config.baseURL || '';
    const url = config.url || '';
    
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    
    return baseURL + url;
  }

  /**
   * 헤더 정규화
   */
  private _normalizeHeaders(headers: any): Record<string, string> {
    const normalized: Record<string, string> = {};
    
    if (!headers) {
      return normalized;
    }

    // axios headers는 다양한 형태일 수 있음
    Object.entries(headers).forEach(([key, value]) => {
      if (typeof value === 'string') {
        normalized[key] = value;
      } else if (value != null) {
        normalized[key] = String(value);
      }
    });

    return normalized;
  }

  /**
   * 헤더 크기 계산
   */
  private _calculateHeadersSize(headers: any): number {
    const normalized = this._normalizeHeaders(headers);
    return Object.entries(normalized).reduce((size, [key, value]) => {
      return size + key.length + value.length + 4; // ": " + "\r\n"
    }, 0);
  }

  /**
   * GraphQL 감지
   */
  private _detectGraphQL(headers: any, body?: string): any {
    const normalized = this._normalizeHeaders(headers);
    const contentType = normalized['content-type'] || normalized['Content-Type'] || '';
    
    if (contentType.includes('application/graphql')) {
      return {
        operationType: this._detectGraphQLOperationType(body || ''),
        operationName: this._extractGraphQLOperationName(body || ''),
      };
    }

    if (contentType.includes('application/json') && body) {
      try {
        const parsed = JSON.parse(body);
        if (parsed.query || parsed.operationName) {
          return {
            operationType: this._detectGraphQLOperationType(parsed.query),
            operationName: parsed.operationName,
            variables: parsed.variables,
            extensions: parsed.extensions,
          };
        }
      } catch (error) {
        // JSON 파싱 실패
      }
    }

    return undefined;
  }

  /**
   * GraphQL 오퍼레이션 타입 감지
   */
  private _detectGraphQLOperationType(query: string): 'query' | 'mutation' | 'subscription' {
    if (!query) return 'query';
    
    const trimmed = query.trim().toLowerCase();
    if (trimmed.startsWith('mutation')) return 'mutation';
    if (trimmed.startsWith('subscription')) return 'subscription';
    return 'query';
  }

  /**
   * GraphQL 오퍼레이션 이름 추출
   */
  private _extractGraphQLOperationName(query: string): string | undefined {
    if (!query) return undefined;
    
    const match = query.match(/(?:query|mutation|subscription)\s+(\w+)/);
    return match ? match[1] : undefined;
  }

  /**
   * GraphQL 응답 파싱
   */
  private _parseGraphQLResponse(body?: string): any {
    if (!body) return undefined;

    try {
      const parsed = JSON.parse(body);
      if (parsed.data !== undefined || parsed.errors !== undefined) {
        return {
          errors: parsed.errors,
          data: parsed.data,
          extensions: parsed.extensions,
        };
      }
    } catch (error) {
      // GraphQL 응답이 아님
    }

    return undefined;
  }

  /**
   * 요청 우선순위 계산
   */
  private _calculatePriority(url: string, method: string): 'high' | 'medium' | 'low' {
    if (['POST', 'PUT', 'DELETE'].includes(method.toUpperCase())) {
      return 'high';
    }

    if (url.includes('/api/') || url.includes('/graphql')) {
      return 'medium';
    }

    return 'low';
  }

  /**
   * 현재 상태 확인
   */
  public get isIntercepting(): boolean {
    return this._isIntercepting;
  }

  /**
   * 상태 정보 가져오기
   */
  public getState() {
    return {
      isIntercepting: this._isIntercepting,
      hasRequestInterceptor: this._requestInterceptorId !== null,
      hasResponseInterceptor: this._responseInterceptorId !== null,
      axiosAvailable: this._getAxiosInstance() !== null,
    };
  }

  /**
   * 정리
   */
  public destroy(): void {
    this.stopIntercepting();
  }
}
